import { UnableToNotifyReason } from './customerUpdatesSlice';
import {
    CustomerUpdatesFilterValues,
    CustomerUpdatesSortableProp,
    deleteUnableToNotifyReason,
    fetchAvailableFilterValues,
    fetchCustomerUpdates,
    fetchUnableToNotifyReasons,
    markCustomerUpdateNotificationStatus,
    upsertUnableToNotifyReason,
} from './customerUpdates.api';
import { CustomerWithStatuses } from '../../../model/customerWithUpdates';
import {
    CustomerUpdatesType,
    ICustomerNotificationActionContext,
} from './customerUpdates.types';
import { CustomerUpdatesTableColumnId } from '../../../components/CustomerUpdatesPage/CustomerUpdatesTable/CustomerUpdatesTable.utils';
import { PagedCollection } from '../../../model/pagedCollection';
import { createAppThunk } from '../../thunks';

export const customerUpdatesDataConstraints = {
    maxArrayFilterLength: 10,
    maxQueryTextLength: 100,
    maxFeedbackMessageLength: 50,
} as const;

const columnIdToSortableProp: Record<
    CustomerUpdatesTableColumnId,
    CustomerUpdatesSortableProp
> = {
    displayName: 'Name',
    flight: 'FlightIata',
    airport: 'FlightAirports',
    departureTime: 'FlightScheduledDepartureTime',
    arrivalTime: 'FlightScheduledArrivalTime',
    seatNumber: 'SeatNumber',
    loyaltyLevel: 'FrequentFlyerTier',
    actions: 'Name',
} as const;

export const loadCustomerUpdatesFilters =
    createAppThunk<CustomerUpdatesFilterValues>(
        'customerUpdates/loadCustomerUpdatesFilters',
        async () => {
            const result = await fetchAvailableFilterValues();
            return result.data;
        }
    );

export const loadCustomerUpdates = createAppThunk<
    PagedCollection<CustomerWithStatuses>
>('customerUpdates/loadCustomerUpdates', async (_, { getState }) => {
    const { customerUpdates } = getState();
    const {
        pagination,
        sorting,
        queryParams: { filters, queryText },
        flightTeamId,
        viewType,
    } = customerUpdates;

    const updatesType: CustomerUpdatesType | undefined =
        viewType === 'All' ? undefined : viewType;

    const { maxArrayFilterLength, maxQueryTextLength } =
        customerUpdatesDataConstraints;

    const resp = await fetchCustomerUpdates({
        flightTeamId,
        updatesType,
        queryText: queryText?.slice(0, maxQueryTextLength),
        mishandledBaggageStatus: filters.baggageStatus ?? undefined,
        misconnectedCustomersStatus:
            filters.misconnectedCustomerStatus ?? undefined,
        messageStatus: filters.messageStatus ?? undefined,
        cabins: filters.cabins.slice(0, maxArrayFilterLength),
        frequentFlyerTiers: filters.loyalties.slice(0, maxArrayFilterLength),
        page: pagination.currentPage,
        pageSize: pagination.pageSize,
        orderBy: columnIdToSortableProp[sorting.sortBy],
        orderByDescending: sorting.sortByDescending,
    });
    return resp.data;
});

export const markCustomerNotificationAction = createAppThunk<
    void,
    ICustomerNotificationActionContext
>('customerUpdates/markCustomerNotificationAction', async (data) => {
    await markCustomerUpdateNotificationStatus(data.customerId, { ...data });
});

export const loadUnableToNotifyReasons = createAppThunk<UnableToNotifyReason[]>(
    'customerUpdates/loadUnableToNotifyReasons',
    async () => {
        return await fetchUnableToNotifyReasons();
    }
);

export const addOrCreateUnableToNotifyReason = createAppThunk<
    void,
    { id?: string; displayName: string }
>(
    'customerUpdates/addOrCreateUnableToNotifyReason',
    async ({ id, displayName }) => {
        await upsertUnableToNotifyReason(id || null, displayName);
    }
);

export const removeUnableToNotifyReason = createAppThunk<void, string>(
    'customerUpdates/removeUnableToNotifyReason',
    async (id) => {
        await deleteUnableToNotifyReason(id);
    }
);
